import { Button as MuiButton, ButtonProps, darken } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { ArrowForward } from "@mui/icons-material";

export type ButtonTone = "primary" | "secondary" | "ghost" | "accent" | "outline" | "dark" | "hero" | "link";

interface Props extends Omit<ButtonProps, "color"> {
  tone?: ButtonTone;
  withArrow?: boolean;
}

const TONE_SX: Record<ButtonTone, SxProps<Theme>> = {
  primary: {
    bgcolor: "primary.main",
    color: "#fff",
    // Si la marca define `primary.hover` se usa tal cual; si no, se oscurece `main` con el filtro.
    "&:hover": (theme) =>
      theme.palette.primary.hover
        ? { bgcolor: theme.palette.primary.hover }
        : { bgcolor: theme.palette.primary.main, filter: "brightness(0.92)" },
    "&.Mui-disabled": { bgcolor: "primary.main", opacity: 0.4, color: "#fff" },
  },
  secondary: {
    bgcolor: "primary.light",
    color: "primary.dark",
    "&:hover": { bgcolor: "primary.base" },
  },
  ghost: {
    bgcolor: "transparent",
    color: "primary.dark",
    "&:hover": { bgcolor: "action.hover" },
  },
  accent: {
    bgcolor: "primary.accent",
    color: "#fff",
    "&:hover": { opacity: 0.9, bgcolor: "primary.accent" },
  },
  // Blanco con borde frío: el "Cancelar" de los modales del diseño, que necesita leerse como
  // botón (a diferencia de `ghost`, que es un texto sin contorno).
  outline: {
    bgcolor: "background.paper",
    color: "primary.dark",
    border: "1px solid #D5E1EC",
    "&:hover": { bgcolor: "#F7FAFD", borderColor: "#D5E1EC" },
    "&.Mui-disabled": { bgcolor: "background.paper", borderColor: "#D5E1EC", opacity: 0.5 },
  },
  // Navy: acción destacada pero neutral, como el "Sí, descartar" del diseño.
  dark: {
    bgcolor: "primary.dark",
    color: "#fff",
    "&:hover": { bgcolor: "primary.dark", filter: "brightness(1.25)" },
    "&.Mui-disabled": { bgcolor: "primary.dark", opacity: 0.4, color: "#fff" },
  },
  // CTA principal de los cotizadores v2 (`.btn-primary.btn-lg.quote-hero-cta`, antes `ButtonCotizador`):
  // 44px, radio 6 y sube 1px en hover. Usar con `withArrow`.
  hero: {
    height: 44,
    px: "20px",
    borderRadius: "6px",
    fontFamily: (theme) => theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: "-0.005em",
    whiteSpace: "nowrap",
    bgcolor: "primary.main",
    color: "#fff",
    transition: "transform .15s ease, background-color .18s ease",
    "& .MuiButton-endIcon svg": { fontSize: 16 },
    "&:hover": {
      bgcolor: (theme) => theme.palette.primary.hover ?? darken(theme.palette.primary.main, 0.3),
      transform: "translateY(-1px)",
    },
    "&:active": { transform: "translateY(0) scale(.985)" },
    "&.Mui-disabled": { bgcolor: "primary.main", color: "#fff", opacity: 0.4 },
  },
  // Acción secundaria como enlace subrayado (ej. descarga de la tabla de amortización del cotizador).
  link: {
    px: 0,
    py: 0,
    minWidth: 0,
    fontFamily: (theme) => theme.typography.fontFamily,
    fontSize: 13,
    fontWeight: 400,
    // El enlace puede ser largo: se parte en varias líneas en vez de ensanchar la pantalla en móvil.
    whiteSpace: "normal",
    textAlign: "left",
    color: "primary.main",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    "&:hover": { bgcolor: "transparent", textDecoration: "underline", color: "primary.dark" },
  },
};

/** Tonos que se pintan como texto (sin fondo de botón). */
const TEXT_TONES: ButtonTone[] = ["ghost", "link"];

export const Button = ({ tone = "primary", withArrow, children, sx, endIcon, ...rest }: Props) => (
  <MuiButton
    variant={TEXT_TONES.includes(tone) ? "text" : "contained"}
    disableElevation
    disableRipple={tone === "link"}
    sx={[
      { borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 3 },
      TONE_SX[tone],
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    endIcon={withArrow ? <ArrowForward fontSize="small" /> : endIcon}
    {...rest}
  >
    {children}
  </MuiButton>
);
